import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelTypesEnum} from '../../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {BasicDialog} from '../../../../../libs/shared/ui/src/components/dialogs/basicDialog';
import {useLinkNavigation} from '../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../models/url/settingsUrlHelpers';
import {getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {useOnboardingModalState} from '../../../app/topBar/onboarding/onboardingContext';
import {IntegrationIconsEnum} from '../../../common/integrationIcon';
import {CellsEmptySetupActionsButton} from '../../../inboxes/cells/content/cellsEmpty/cellsEmptySetupActionsButton';
import {OnboardingGuidesEnum} from '../../guide/onboardingGuideTypes';

/*
 * Props.
 */

interface FluAddEmailPopoverProps {}

/*
 * Style.
 */

const StyledDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding-top: 25px;
`;

/*
 * Component.
 */

export const FluAddEmailPopover: FC<FluAddEmailPopoverProps> = () => {
  const {navigateToLink} = useLinkNavigation();
  const {onboardingGuide, setOnboardingGuide} = useOnboardingModalState();
  const userNamespace = useAppSelector(getUserNamespace);

  if (
    onboardingGuide !== OnboardingGuidesEnum.FRONT_LINE_USER_ADD_EMAIL_POPOVER &&
    onboardingGuide !== OnboardingGuidesEnum.FRONT_LINE_USER_SEND_EMAIL_POPOVER
  ) {
    return null;
  }

  return (
    <BasicDialog
      width={400}
      title={<Trans id="flu-add-email-popover-title">Add your work email</Trans>}
      onRequestClose={() => setOnboardingGuide(undefined)}
    >
      <StyledDiv>
        {onboardingGuide === OnboardingGuidesEnum.FRONT_LINE_USER_SEND_EMAIL_POPOVER && (
          <Trans id="flu-add-email-popover-subtitle">
            To send your first email, add your work email to Front
            <br /> (e.g. sophia@company.com)
          </Trans>
        )}
        <StyledButtonWrapper>
          <CellsEmptySetupActionsButton
            buttonVariant={ButtonVariantsEnum.PRIMARY}
            interactionId="flu-add-email-popover-google"
            title={<Trans id="flu-add-email-popover-google">Add with Google</Trans>}
            integrationIcon={IntegrationIconsEnum.GOOGLE}
            onClick={() => {
              setOnboardingGuide(undefined);
              navigateToLink(
                buildSettingsPath({
                  namespace: userNamespace,
                  rootPage: SettingsRootPagesEnum.CHANNELS,
                  resourcePage: SettingsResourcePagesEnum.NEW,
                  channelType: ChannelTypesEnum.GMAIL,
                }),
              );
            }}
          />
          <CellsEmptySetupActionsButton
            buttonVariant={ButtonVariantsEnum.PRIMARY}
            interactionId="flu-add-email-popover-microsoft"
            title={<Trans id="flu-add-email-popover-microsoft">Add with Microsoft</Trans>}
            integrationIcon={IntegrationIconsEnum.MICROSOFT}
            onClick={() => {
              setOnboardingGuide(undefined);
              navigateToLink(
                buildSettingsPath({
                  namespace: userNamespace,
                  rootPage: SettingsRootPagesEnum.CHANNELS,
                  resourcePage: SettingsResourcePagesEnum.NEW,
                  channelType: ChannelTypesEnum.OFFICE365,
                }),
              );
            }}
          />
          <CellsEmptySetupActionsButton
            buttonVariant={ButtonVariantsEnum.SECONDARY}
            interactionId="flu-add-email-popover-other"
            title={<Trans id="flu-add-email-popover-other">Other email account</Trans>}
            onClick={() => {
              setOnboardingGuide(undefined);
              navigateToLink(
                buildSettingsPath({
                  namespace: userNamespace,
                  rootPage: SettingsRootPagesEnum.CHANNELS,
                  resourcePage: SettingsResourcePagesEnum.NEW,
                  channelType: ChannelTypesEnum.SMTP,
                }),
              );
            }}
          />
        </StyledButtonWrapper>
      </StyledDiv>
    </BasicDialog>
  );
};
